<template>
  <my-modal
    v-show="modelValue"
    @close="closeModal"
  >
    <template #header>
      <div style="width: 100%; display: flex; justify-content: space-between; align-items: center;">
        <h3 style="margin: 0;">Выбрать ТС</h3>
        <ui-close-button @click="closeModal" />
      </div>
    </template>

    <template #body>
      <div class="vehicle-select-modal-body">
        <form
          id="vehicle-dashboard-objects-list-form"
          @submit.prevent
        >
          <div class="monitoring__container text-center">
            <div
              class="flex monitoring-sidebar__form"
              style="width: 100%; justify-content: center; gap: 5px;"
            >
              <div class="flex">
                <ui-image-button
                  style="margin-left: 5px"
                  path="/images/checked-checkbox-26.png"
                  alt="check"
                  targetText="Выделить все объекты"
                  @click="checkAllObjects"
                  @mouseenter="addHoverOnTarget($event)"
                  @mouseleave="removeHoverOnTarget($event)"
                />

                <ui-image-button
                  path="/images/cleared-checkbox-26.png"
                  alt="clear"
                  targetText="Очистить выделение всех объектов"
                  @click="clearAllObjects"
                  @mouseenter="addHoverOnTarget($event)"
                  @mouseleave="removeHoverOnTarget($event)"
                />
              </div>
              <div class="flex monitoring-sidebar__form-filter">
                <!-- Показываем ui-select вместо ui-filter, если выбран только client -->
                <ui-select
                  v-if="isClientFilterMode"
                  ref="clientSelectRef"
                  v-model:select="props.clientSearchStr"
                  placeholder="Владельцы"
                  :items="clientList"
                  class="monitoring-sidebar__filter"
                  :scrollable="true"
                  :custom-select-style="true"
                  :searchable="true"
                  multiple
                />
                <ui-filter
                  v-else
                  :text="props.searchStr"
                  input-class="monitoring-sidebar__filter"
                  type="text"
                  placeholder="Фильтр объектов"
                  targetText="Фильтр списка объектов. Можно перечислять через запятую"
                  :timer-handler="showObjectsListItemsHandler"
                  @update:text="(val) => emit('update:searchStr', val)"
                  @mouseenter="addHoverOnTarget($event)"
                  @mouseleave="removeHoverOnTarget($event)"
                />

                <ui-count
                  :count-filtered="countFiltered"
                  :count-all="Object.keys(showObjectsKeys).length"
                  targetText="Показано объектов / всего доступно"
                  @mouseenter="addHoverOnTarget($event)"
                  @mouseleave="removeHoverOnTarget($event)"
                />
              </div>
              <div class="flex monitoring-sidebar__filter-footer">
                <div class="d-inline" style="width: 110px;">
                  <ui-select
                    ref="filterSelectRef"
                    class="monitoring__filter-select"
                    :select="props.filterSelected"
                    placeholder="Не выбрано"
                    multiple
                    :items="FILTER_NAMES"
                    @update:select="(val) => emit('update:filterSelected', val)"
                  />
                </div>
              </div>
            </div>
          </div>

          <objects-list
            :interval="datepickerDate"
            :show-table-columns="columnsToObject(selectedTableColumnsArr)"
            :isfor-monitoring="false"
            :isforstatistics="true"
            :is-resizable="false"
            v-model:show-objects-keys="showObjectsKeys"
            v-model:checked-objects-keys="checkedObjectsKeys"
          />
        </form>
      </div>
    </template>
    
    <template #footer>
      <div style="width: 100%; display: flex; justify-content: flex-end; gap: 10px;">
        <button class="btn btn-secondary" @click="closeModal">Отмена</button>
        <button class="btn btn-primary" @click="applySelection">Применить</button>
      </div>
    </template>
  </my-modal>
</template>

<script setup>
import { computed, inject, onMounted, onUnmounted, ref, watch } from 'vue';

import MyModal from '@/App/Components/MyModal.vue';
import UiCloseButton from '@/App/Components/Buttons/UiCloseButton.vue';
import UiImageButton from '../../Monitoring/Sidebar/components/UiImageButton.vue';
import UiFilter from '../../Monitoring/Sidebar/components/UiFilter.vue';
import UiCount from '../../Monitoring/Sidebar/components/UiCount.vue';
import UiSelect from '../../Monitoring/Sidebar/components/UiSelect.vue';
import ObjectsList from '../../Monitoring/Sidebar/components/ObjectsList.vue';

import { isTimeEqualZero } from '@/helpers/MineHelper';
import {
  addHoverOnTarget,
  removeHoverOnTarget,
} from '../../helpers/targetsHelper';
import { DiagnosticSettings } from '@/App/Diagnostics/default/DiagnosticSettings';

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  selectedTsList: {
    type: Array,
    default: () => [],
  },
  filterSelected: {
    type: Array,
    default: () => ['name'],
  },
  searchStr: {
    type: String,
    default: '',
  },
  clientSearchStr: { 
    type: Array, 
    default: () => [] 
  }
});

const emit = defineEmits(['update:modelValue', 'update:clientSearchStr', 'apply', 'update:filterSelected', 'update:searchStr']);

// Refs для ui-select компонентов
const clientSelectRef = ref(null);
const filterSelectRef = ref(null);

const FILTER_NAMES = ref([
  {
    value: 'name',
    text: 'Наименование',
  },
  {
    value: 'stateNumber',
    text: 'Гос №',
  },
  {
    value: 'last_time_aggregation',
    text: 'Готовность отчета',
  },
  {
    value: 'install_time',
    text: 'Дата установки',
  },
  {
    value: 'lat',
    text: 'Широта',
  },
  {
    value: 'lon',
    text: 'Долгота',
  },
  {
    value: 'client',
    text: 'Владелец',
  },
  {
    value: 'model',
    text: 'Модель',
  },
  {
    value: 'vin',
    text: 'VIN',
  },
  {
    value: 'category',
    text: 'Категория',
    items: [
      {
        value: 'category-0',
        text: 'Все объекты',
        icon: '/images/categories/all-objects.png',
        items: [
          {
            value: 'category-101',
            text: 'Отключен',
            icon: '/images/categories/disabled.png',
          },
        ],
      },
      {
        value: 'category-1',
        text: 'Статичный объект',
        icon: '/images/categories/home.png',
        items: [
          {
            value: 'category-11',
            text: 'ДЭС',
            icon: '/images/categories/des.png',
          },
          {
            value: 'category-12',
            text: 'Кран',
            icon: '/images/categories/crane.png',
          },
          {
            value: 'category-13',
            text: 'АЗС',
            icon: '/images/categories/gas.png',
          },
        ],
      },
      {
        value: 'category-2',
        text: 'Легковой транспорт',
        icon: '/images/categories/passenger_car.png',
        items: [
          {
            value: 'category-21',
            text: 'Пикап',
            icon: '/images/categories/pickup.png',
          },
        ],
      },
      {
        value: 'category-3',
        text: 'Грузовики',
        icon: '/images/categories/truck.png',
        items: [
          {
            value: 'category-31',
            text: 'Самосвал',
            icon: '/images/categories/samosval.png',
          },
          {
            value: 'category-32',
            text: 'Седельный тягач',
            icon: '/images/categories/tyagach.png',
          },
          {
            value: 'category-33',
            text: 'Автобус на базе груз.тс',
            icon: '/images/categories/gruz-bus.png',
          },
          {
            value: 'category-34',
            text: 'Топливозаправщик',
            icon: '/images/categories/tanker.png',
          },
        ],
      },
      {
        value: 'category-4',
        text: 'Спец.техника',
        icon: '/images/categories/asfaltucladchick.png',
        items: [
          {
            value: 'category-41',
            text: 'Бульдозер',
            icon: '/images/categories/bulldozer.png',
          },
          {
            value: 'category-42',
            text: 'Трактор',
            icon: '/images/categories/tractor.png',
          },
          {
            value: 'category-43',
            text: 'Экскаватор',
            icon: '/images/categories/excavator.png',
          },
          {
            value: 'category-44',
            text: 'Каток',
            icon: '/images/categories/rink.png',
          },
          {
            value: 'category-45',
            text: 'Погрузчик',
            icon: '/images/categories/pogruzchick.png',
          },
          {
            value: 'category-46',
            text: 'Асфальтоукладчик',
            icon: '/images/categories/asfaltucladchick.png',
          },
          {
            value: 'category-47',
            text: 'Дорожная фреза',
            icon: '/images/categories/road-milling-cutter.png',
          },
        ],
      },
      {
        value: 'category-5',
        text: 'Автокран',
        icon: '/images/categories/auto-crane.png',
      },
      {
        value: 'category-6',
        text: 'Автогрейдер',
        icon: '/images/categories/autograider.png',
      },
      {
        value: 'category-7',
        text: 'Автобусы',
        icon: '/images/categories/bus.png',
        items: [
          {
            value: 'category-71',
            text: 'Микроавтобусы',
            icon: '/images/categories/mini-bus.png',
          },
        ],
      },
      {
        value: 'category-8',
        text: 'VIP',
        icon: '/images/categories/vip.png',
      },
      {
        value: 'category-9',
        text: 'Флот',
        icon: '/images/categories/flot.png',
      },
      {
        value: 'category-10',
        text: 'Вездеход',
        icon: '/images/categories/vezdehod.png',
      },
    ],
  },
]);

const globalObjectsList = inject('globalObjectsList');
const objectsListUpdateTrigger = inject('objectsListUpdateTrigger');

const datepickerDate = ref([]);
const showObjectsKeys = ref({});
const checkedObjectsKeys = ref([]);
const selectedTableColumnsArr = ref(
  DiagnosticSettings.checkedObjectsListTableSettings,
);
const countFilteredObjects = ref(null);
// const clientSearchStr = ref([]); // Поиск по client в ui-select (массив для множественного выбора)

// Список уникальных client из globalObjectsList
const clientList = computed(() => {
  const clients = new Set();
  for (const id in globalObjectsList) {
    const object = globalObjectsList[id];
    if (object && object.client) {
      clients.add(object.client);
    }
  }
  return Array.from(clients).map((client) => ({
    value: client,
    text: client,
  }));
});

// Флаг: показывать ui-select вместо ui-filter (когда выбран только client)
const isClientFilterMode = computed(() => {
  return (props.filterSelected || []).length === 1 && (props.filterSelected || [])[0] === 'client';
});

const countFiltered = computed(() => {
  return countFilteredObjects.value ?? Object.keys(globalObjectsList).length;
});

const columnsToObject = (columnsArr) => {
  const obj = {};
  for (let i = 0; i < columnsArr.length; i++) {
    obj[columnsArr[i]['name']] = columnsArr[i];
  }
  return obj;
};

const checkAllObjects = () => {
  const curCheckedObjectsArr = Object.keys(showObjectsKeys.value).filter(
    (key) => showObjectsKeys.value[key] === true,
  );
  checkedObjectsKeys.value = [
    ...new Set([...checkedObjectsKeys.value, ...curCheckedObjectsArr]),
  ];
};

const clearAllObjects = () => {
  checkedObjectsKeys.value.length = 0;
};

const showObjectsListItemsHandler = () => {
  const phrases = (props.searchStr || '')
    .toLowerCase()
    .split(',')
    .map((p) => p.trim());
  const objectsIds = Object.keys(showObjectsKeys.value);

  const categoryParams = (props.filterSelected || []).filter((filterName) =>
    filterName.startsWith('category-'),
  );

  let countOfShownObjects = 0;

  for (const id of objectsIds) {
    const object = globalObjectsList[id];

    let isInCategories = categoryParams.length ? false : true;
    for (const param of categoryParams) {
      const category = param.replace('category-', '');

      if (category == object.category) {
        isInCategories = true;
        break;
      }
    }
    if (!isInCategories) {
      showObjectsKeys.value[id] = false;
      continue;
    }

    let isShow = true;
    for (const phrase of phrases) {
      for (const param of (props.filterSelected || [])) {
        if (categoryParams.includes(param)) {
          continue;
        }

        if (['last_time_aggregation', 'install_time'].includes(param)) {
          const stringTime = isTimeEqualZero(object[param]);
          isShow = stringTime.includes(phrase);
        } else {
          const value = object[param].toString().toLowerCase();
          isShow = value.includes(phrase);
        }

        if (isShow) break;
      }
      if (isShow) break;
    }
    showObjectsKeys.value[id] = isShow;
    countOfShownObjects += isShow ? 1 : 0;
  }

  countFilteredObjects.value = countOfShownObjects;
};

const closeModal = () => {
  emit('update:modelValue', false);
};

const applySelection = () => {
  emit('apply', [...checkedObjectsKeys.value]);
  emit('update:modelValue', false);
};

watch(objectsListUpdateTrigger, () => {
  const shownObjectsListKeys = Object.keys(showObjectsKeys.value);
  const objectsListKeys = Object.keys(globalObjectsList);

  if (
    JSON.stringify(shownObjectsListKeys) !== JSON.stringify(objectsListKeys)
  ) {
    for (let key in showObjectsKeys.value) {
      if (globalObjectsList[key]) {
        showObjectsKeys.value[key] = false;
      } else {
        delete showObjectsKeys.value[key];
      }
    }

    for (let i = 0; i < objectsListKeys.length; i++) {
      showObjectsKeys.value[objectsListKeys[i]] = true;
    }
  }

  // В режиме client filter фильтрация выполняется через watch(props.clientSearchStr)
  if (isClientFilterMode.value) {
    // Запускаем фильтрацию по client, если есть выбранное значение
    if (props.clientSearchStr && props.clientSearchStr.length > 0) {
      // Триггерим фильтрацию
      const selectedClients = props.clientSearchStr.map(c => c.toString().toLowerCase());
      for (const id of objectsListKeys) {
        const object = globalObjectsList[id];
        const clientValue = object?.client?.toString().toLowerCase() || '';
        showObjectsKeys.value[id] = selectedClients.includes(clientValue);
      }
      countFilteredObjects.value = objectsListKeys.filter(id => showObjectsKeys.value[id]).length;
    }
  } else if (props.searchStr) {
    showObjectsListItemsHandler();
  }
});

// Следим за изменениями filterSelected и searchStr из родителя
watch([() => props.filterSelected, () => props.searchStr], () => {
  showObjectsListItemsHandler();
}, { deep: true });

// Сброс props.clientSearchStr при переключении из режима client filter
watch(() => props.filterSelected, (newVal) => {
  if ((newVal || []).length !== 1 || (newVal || [])[0] !== 'client') {
    props.clientSearchStr = [];
  }
}, { deep: true });

// Watch для фильтрации по client в режиме ui-select
watch(props.clientSearchStr, () => {
  const objectsIds = Object.keys(showObjectsKeys.value);

  if (isClientFilterMode.value && props.clientSearchStr && props.clientSearchStr.length > 0) {
    // Режим множественного выбора client
    const selectedClients = props.clientSearchStr.map(c => c.toString().toLowerCase());

    for (const id of objectsIds) {
      const object = globalObjectsList[id];
      const clientValue = object?.client?.toString().toLowerCase() || '';

      const isShow = selectedClients.includes(clientValue);
      showObjectsKeys.value[id] = isShow;
    }

    countFilteredObjects.value = objectsIds.filter(id => showObjectsKeys.value[id]).length;
  } else if (isClientFilterMode.value && (!props.clientSearchStr || props.clientSearchStr.length === 0)) {
    // Если не выбран client, показываем все объекты
    for (const id of objectsIds) {
      showObjectsKeys.value[id] = true;
    }
    countFilteredObjects.value = objectsIds.length;
  }
}, { deep: true });

// Следим за изменениями selectedTsList из родителя
watch(() => props.selectedTsList, (newVal) => {
  if (newVal && newVal.length > 0) {
    checkedObjectsKeys.value = [...newVal];
  }
}, { immediate: true });

onMounted(() => {
  const d = new Date();
  const from = new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate() - 1,
    0,
    0,
    0,
  );
  const to = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0);
  datepickerDate.value = [from.toString(), to.toString()];

  // Инициализируем showObjectsKeys и checkedObjectsKeys из глобального списка
  for (let key in globalObjectsList) {
    showObjectsKeys.value[key] = true;
  }

  // Если есть выбранные ТС из родителя, используем их
  if (props.selectedTsList && props.selectedTsList.length > 0) {
    checkedObjectsKeys.value = [...props.selectedTsList];
  }
});
</script>

<style lang="scss" scoped>
.vehicle-select-modal-body {
  height: 100%;
  overflow: auto;
  padding: 10px;
}

.monitoring__container {
  width: 100%;
}

.monitoring-sidebar__form {
  justify-content: space-between;
  @media (max-width: 768px) {
    justify-content: space-around;
    flex-wrap: wrap;
  }
}

.monitoring-sidebar__form-filter {
  @media (max-width: 450px) {
    width: 75%;
  }
}

.monitoring-sidebar__filter {
  width: 160px;

  @media (max-width: 768px) {
    width: auto;
  }
}

.monitoring-sidebar__filter-footer {
  & .d-inline {
    & select {
      padding: 0 5px;
      width: 125px;
    }
  }
  @media (max-width: 450px) {
    width: 100%;

    & .d-inline {
      width: 100%;
    }
  }
}
</style>
